import { Formik } from "formik";
import React, { FC, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import HeaderView from "../../components/HeaderView";
import SubHeaderView from "../../components/SubHeaderView";
import HarshDriving from "./harsh-driving";
import Impact from "./impact";
import { sensorEventDetectionFormInitialValues } from "../../validation";
import { AdminFormFieldRadioCheckbox } from "../../../../components/admin/formFields";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  useGetCameraConfigQuery,
  useUpdateConfigurationSingleDeviceMutation,
} from "../../../../api/network/vmApiService";
import { toast } from "react-toastify";
import { SensorConfiguration } from "../../../../api/types/CamerasAdminVM";
import { routeUrls } from "../../../../navigation/routeUrls";
import { useCameraAdminContext } from "../../context/cameraAdmin.context";
import * as Yup from "yup";
import useConversion from "../../../../hooks/conversion";
import { REPORT_FIELD_OUTPUT_TYPES } from "../../../reports/constants";
import { SPEED_UNITS } from "../../../admin-details/account/constants";
import { convertKmphTo, convertSpeedToKmph } from "../../../../utils/distance";
import { APP_CONFIG } from "../../../../constants/constants";
import svgIcons from "../../../../assets/svg/svgIcons";
import { useDispatch, useSelector } from "react-redux";
import { CameraDeviceConfigProfile } from "../../../../api/types/Camera";
import { setSingleVMCameraCurrentConfigs } from "../../../../api/store/commonSlice";

const ScreenSensorEventDetection: FC = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.sensor_event_detection",
  });

  const navigationTypeRef = useRef<"none" | "next" | "previous">("none");
  const { getUnitByOutputType } = useConversion();

  const getSpeedUnit = () => {
    return getUnitByOutputType(REPORT_FIELD_OUTPUT_TYPES.KPH_TO_SPEED_UNITS);
  };

  const getKmphToSpeed = (value: number) => {
    let result = convertKmphTo(getSpeedUnit() as SPEED_UNITS, value ?? 0);
    return result.toFixed(2);
  };

  const getSpeedToKmph = (value: number) => {
    let result = convertSpeedToKmph(getSpeedUnit() as SPEED_UNITS, value ?? 0);
    return result.toFixed(2);
  };

  const sensorEventDetectionFormValidationSchema = Yup.object().shape({
    impact_pre_event_video_duration_sec: Yup.number()
      .required("Pre-event video duration is required")
      .min(6, "Pre-event video duration must be at least 6 second")
      .max(15, "Pre-event video duration cannot exceed 15 seconds"),
    impact_post_event_video_duration_sec: Yup.number()
      .required("Post-event video duration is required")
      .min(2, "Post-event video duration must be at least 2 second")
      .max(30, "Post-event video duration cannot exceed 30 seconds"),
    harsh_pre_event_video_duration_sec: Yup.number()
      .required("Pre-event video duration is required")
      .min(6, "Pre-event video duration must be at least 6 second")
      .max(15, "Pre-event video duration cannot exceed 15 seconds"),
    harsh_post_event_video_duration_sec: Yup.number()
      .required("Post-event video duration is required")
      .min(2, "Post-event video duration must be at least 2 second")
      .max(30, "Post-event video duration cannot exceed 30 seconds"),
    harsh_acceleration_speed: Yup.number()
      .required("Harsh acceleration speed is required")
      .min(
        Number(getKmphToSpeed(1)),
        `Harsh acceleration speed must be at least ${Number(getKmphToSpeed(1))}`
      )
      .max(
        Number(getKmphToSpeed(30)),
        `Harsh acceleration speed must not exceed ${Number(getKmphToSpeed(30))}`
      ),

    harsh_braking_speed: Yup.number()
      .required("Harsh braking speed is required")
      .min(
        Number(getKmphToSpeed(8)),
        `Harsh braking speed must be at least ${Number(getKmphToSpeed(8))}`
      )
      .max(
        Number(getKmphToSpeed(20)),
        `Harsh braking speed must not exceed ${Number(getKmphToSpeed(20))}`
      ),

    harsh_cornering_speed: Yup.number()
      .required("Harsh cornering speed is required")
      .min(
        Number(getKmphToSpeed(10)),
        `Harsh cornering speed must be at least ${Number(getKmphToSpeed(10))}`
      )
      .max(
        Number(getKmphToSpeed(50)),
        `Harsh cornering speed must not exceed ${Number(getKmphToSpeed(50))}`
      ),
  });
  const { cameraId } = useParams<{ cameraId: string }>();
  const { state: deviceData } = useLocation();
  const model = deviceData?.model || "";
  const isSingleConfiguration = deviceData?.isSingleConfiguration || false;
  const cameraName = deviceData?.cameraName || null;
  const isMassSingleConfiguration = deviceData?.isMassSingleConfiguration || false;
  const cameraIds = deviceData?.cameraIds || [];
  const [selectedSettings, setSelectedSettings] = useState<string | null>(
    "impact"
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const allCameraConfigsInStore = useSelector(
    (state: {
      commonReducer: {
        singleVMCameraCurrentConfigs: {
          [key: string]: CameraDeviceConfigProfile;
        };
      };
    }) => state.commonReducer.singleVMCameraCurrentConfigs
  );
  const thisCameraConfigInStore =
    allCameraConfigsInStore?.[cameraId as string] || {};

  const [updateConfigurationSingleApiDeviceTrigger] =
    useUpdateConfigurationSingleDeviceMutation();

  const handleLeftNavigation = () => {
    navigate(
      isSingleConfiguration || isMassSingleConfiguration
        ? `${routeUrls.dashboardChildren.cameraChildren.camera_settings}/${cameraId}`
        : routeUrls.dashboardChildren.cameraChildren.camera_settings,
      {
        state: {
          isSingleConfiguration: isSingleConfiguration,
          isMassSingleConfiguration: isMassSingleConfiguration,
          cameraIds: cameraIds,
          model: model,
          cameraName: cameraName
        },
      }
    );
  };

  const handleRightNavigation = () => {
    navigate(
      isSingleConfiguration || isMassSingleConfiguration
        ? `${routeUrls.dashboardChildren.cameraChildren.ai_event_detection_adas}/${cameraId}`
        : routeUrls.dashboardChildren.cameraChildren.ai_event_detection_adas,
      {
        state: {
          isSingleConfiguration: isSingleConfiguration,
          isMassSingleConfiguration: isMassSingleConfiguration,
          cameraIds: cameraIds,
          model: model,
          cameraName: cameraName
        },
      }
    );
  };

  const {
    data: dataSingleCameraConfig,
    isFetching: isFetchingSingleCameraConfig,
  } = useGetCameraConfigQuery(cameraId || "", {
    skip: (!isSingleConfiguration && !isMassSingleConfiguration)|| !cameraId,
    refetchOnMountOrArgChange: true,
  });

  const memoizedInitialValues = React.useMemo(() => {
    const initialValues = {
      ...sensorEventDetectionFormInitialValues(!isSingleConfiguration && !isMassSingleConfiguration),
      harsh_acceleration_speed: Number(getKmphToSpeed(29.9981722)),
      harsh_braking_speed: Number(getKmphToSpeed(20.0041459)),
      harsh_cornering_speed: Number(getKmphToSpeed(50.0023181)),
    };
    if (!dataSingleCameraConfig?.data?.current || !cameraId)
      return initialValues;
    const {
      data: { current },
    } = dataSingleCameraConfig;
    // here we seed store with freshly fetched configuration
    setTimeout(() => {
      dispatch(
        setSingleVMCameraCurrentConfigs({
          cid: cameraId,
          profile: current,
        })
      );
    }, 100);
    return {
      ...initialValues,
      impact_enable: current.sensor.impactEnable,
      harsh_enable: current.sensor.harshEnable,
      driving_impact: current.sensor.impact_drivingSensitive,
      parking_impact: current.sensor.impact_parkingSensitive,
      impact_pre_event_video_duration_sec: current.sensor.impact_videoBefore,
      impact_post_event_video_duration_sec: current.sensor.impact_videoAfter,
      impact_in_cabin_audio_alert: current.sensor.impact_audioAlert,
      harsh_acceleration_sensitivity: current.sensor.harsh_haSensitive,
      harsh_braking_sensitivity: current.sensor.harsh_hbSensitive,
      harsh_cornering_sensitivity: current.sensor.harsh_hcSensitive,
      harsh_acceleration_speed: current.sensor.harsh_haSpeed
        ? getSpeedUnit() === SPEED_UNITS.KMH
          ? current.sensor.harsh_haSpeed
          : Number(getKmphToSpeed(current.sensor.harsh_haSpeed))
        : 0, // Number(getKmphToSpeed(1)),
      harsh_braking_speed: current.sensor.harsh_hbSpeed
        ? getSpeedUnit() === SPEED_UNITS.KMH
          ? current.sensor.harsh_hbSpeed
          : Number(getKmphToSpeed(current.sensor.harsh_hbSpeed))
        : 0, // Number(getKmphToSpeed(1)),
      harsh_cornering_speed: current.sensor.harsh_hcSpeed
        ? getSpeedUnit() === SPEED_UNITS.KMH
          ? current.sensor.harsh_hcSpeed
          : Number(getKmphToSpeed(current.sensor.harsh_hcSpeed))
        : 0, // Number(getKmphToSpeed(1)),
      harsh_pre_event_video_duration_sec: current.sensor.harsh_videoBefore,
      harsh_post_event_video_duration_sec: current.sensor.harsh_videoAfter,
      harsh_in_cabin_audio_alert: 
        current.sensor.harsh_audioAlert ||
        current.sensor.harsh_haAudioAlert ||
        current.sensor.harsh_hbAudioAlert ||
        current.sensor.harsh_hcAudioAlert,
    };
  }, [isSingleConfiguration, isMassSingleConfiguration ,dataSingleCameraConfig]);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={memoizedInitialValues}
      validationSchema={sensorEventDetectionFormValidationSchema}
      onSubmit={(values, { setSubmitting }) => {
        const navigationType = navigationTypeRef.current;
        const sensorPayloadImpact: SensorConfiguration = values.impact_enable
          ? {
              impactEnable: values.impact_enable,
              impact_drivingSensitive: values.driving_impact,
              impact_parkingSensitive: values.parking_impact,
              impact_videoBefore: values.impact_pre_event_video_duration_sec,
              impact_videoAfter: values.impact_post_event_video_duration_sec,
              impact_audioAlert: values.impact_in_cabin_audio_alert,
            }
          : {
              impactEnable: values.impact_enable,
            };

        const sensorPayloadHarshDriving: SensorConfiguration =
          values.harsh_enable
            ? {
                harshEnable: values.harsh_enable,
                harsh_haSensitive: values.harsh_acceleration_sensitivity,
                harsh_hbSensitive: values.harsh_braking_sensitivity,
                harsh_hcSensitive: values.harsh_cornering_sensitivity,
                harsh_haSpeed: Math.round(
                  Number(getSpeedToKmph(values.harsh_acceleration_speed))
                ),
                harsh_hbSpeed: Math.round(
                  Number(getSpeedToKmph(values.harsh_braking_speed))
                ),
                harsh_hcSpeed: Math.round(
                  Number(getSpeedToKmph(values.harsh_cornering_speed))
                ),
                harsh_videoAfter: values.harsh_post_event_video_duration_sec,
                harsh_audioAlert: values.harsh_in_cabin_audio_alert,
                harsh_haAudioAlert: values.harsh_in_cabin_audio_alert,
                harsh_hbAudioAlert: values.harsh_in_cabin_audio_alert,
                harsh_hcAudioAlert: values.harsh_in_cabin_audio_alert,
              }
            : {
                harshEnable: values.harsh_enable,
              };
        // Merge both payloads
        const combinedSensorPayload: SensorConfiguration = {
          ...sensorPayloadImpact,
          ...sensorPayloadHarshDriving,
        };
        const updateAllCameras = async () => {
          try {
            for (const id of cameraIds) {
              await updateConfigurationSingleApiDeviceTrigger({
                cameraId: id,
                sensor: combinedSensorPayload,
              }).unwrap();
            }
            toast.success("All cameras updated successfully");
            if (navigationType === "next") {
              handleRightNavigation();
            } else if (navigationType === "previous") {
              handleLeftNavigation();
            }
          } catch (err: any) {
            console.error(err);
            toast.error("Failed to update one or more cameras");
          } finally {
            setSubmitting(false);
          }
        };
        if (isSingleConfiguration || isMassSingleConfiguration) {
          updateConfigurationSingleApiDeviceTrigger({
            cameraId: cameraId,
            sensor: combinedSensorPayload,
            // use complete cached object
            // cameraId: cameraId,
            // ...thisCameraConfigInStore,
            // sensor: {
            //   ...thisCameraConfigInStore.sensor,
            //   ...combinedSensorPayload,
            // },
          })
            .unwrap()
            .then((data) => {
              if (data.message) {
                // update store with updated camera config
                dispatch(
                  setSingleVMCameraCurrentConfigs({
                    cid: cameraId as string,
                    profile: {
                      ...thisCameraConfigInStore,
                      sensor: {
                        ...thisCameraConfigInStore.sensor,
                        ...combinedSensorPayload,
                      },
                    },
                  })
                );
                toast.success("Updated Successfully");
                if (navigationType === "next") {
                  handleRightNavigation();
                } else if (navigationType === "previous") {
                  handleLeftNavigation();
                }
              } else {
                toast.error("Updation Failed");
              }
            })
            .catch((errors) => {
              console.error(errors);
              toast.error(errors?.join(""));
            })
            .finally(() => {
              setSubmitting(false);
            });
        } else {
          updateAllCameras();
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        setFieldTouched,
        isSubmitting,
        dirty,
        isValid,
        resetForm,
      }) => {
        return (
          <div className="p-4 bg-gray-100 relative">
            {isFetchingSingleCameraConfig && (
              <div className="absolute w-full h-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white/70 z-10 flex items-center justify-center">
                <img
                  src={svgIcons.LoadingAnimation}
                  alt=""
                  className="size-12"
                />
              </div>
            )}
            {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
              <div className="flex text-[10px]">
                <pre className="flex w-1/3 whitespace-break-spaces break-all">
                  {JSON.stringify(memoizedInitialValues, null, 2)}
                </pre>
                <pre className="flex w-1/3 whitespace-break-spaces break-all">
                  {JSON.stringify(values, null, 2)}
                </pre>
                <pre className="flex w-1/3 whitespace-break-spaces break-all max-h-72 overflow-auto">
                  {JSON.stringify(thisCameraConfigInStore, null, 2)}
                </pre>
              </div>
            )}
            <HeaderView
              heading={t("heading")}
              handleSave={() => {
                navigationTypeRef.current = "none";
                handleSubmit();
              }}
              handleSaveAndNext={() => {
                navigationTypeRef.current = "next";
                handleSubmit();
              }}
              handlePrevious={() => {
                navigationTypeRef.current = "previous";
                handleSubmit();
              }}
              handleLeftNavigation={handleLeftNavigation}
              handleRestoreToDefault={() => resetForm()}
              disabled={
                !isValid ||
                isSubmitting ||
                (!isSingleConfiguration && !isMassSingleConfiguration && cameraIds?.length < 1)
              }
              index={1}
            />
            <div className="bg-white grid grid-cols-12">
              <SubHeaderView
                isSingleConfiguration={isSingleConfiguration}
                isMassSingleConfiguration={isMassSingleConfiguration}
                noOfCamera={cameraIds?.length}
                cameraName={cameraName}
              />
              <form
                className="col-span-12 grid grid-cols-12"
                onSubmit={handleSubmit}
              >
                <div className="col-span-3 border-r">
                  <div
                    className={`w-full font-semibold text-sm leading-none tracking-normal px-5 py-4 cursor-pointer hover:bg-accent-blue-paleO66 cursor-pointer`}
                    onClick={() => {
                      setSelectedSettings("impact");
                    }}
                  >
                    <AdminFormFieldRadioCheckbox
                      label={t("impact")}
                      radioChecked={selectedSettings === "impact"}
                      checked={values.impact_enable}
                      onCheckboxChange={(checked)=>{setFieldValue("impact_enable",checked)}}
                      isRadioButton={true}
                      onRadioChange={()=>setSelectedSettings("impact")}
                    />
                  </div>
                  <div
                    className={`w-full font-semibold text-sm leading-none tracking-normal px-5 py-4 cursor-pointer hover:bg-accent-blue-paleO66 cursor-pointer`}
                    onClick={(e) => {
                      setSelectedSettings("harsh_driving");
                    }}
                  >                    
                    <AdminFormFieldRadioCheckbox
                      label={t("harsh_driving")}
                      radioChecked={selectedSettings === "harsh_driving"}
                      checked={values.harsh_enable}
                      onCheckboxChange={(checked)=>{setFieldValue("harsh_enable",checked)}}
                      isRadioButton={true}
                      onRadioChange={()=>setSelectedSettings("harsh_driving")}
                    />
                  </div>
                </div>
                <div
                  className={`col-span-9 grid grid-cols-6 gap-6 ${
                    (selectedSettings === "impact" && !values.impact_enable) ||
                    (selectedSettings === "harsh_driving" &&
                      !values.harsh_enable)
                      ? "pointer-events-none opacity-50"
                      : ""
                  }`}
                >
                  {selectedSettings === "impact" ? (
                    <Impact
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      handleBlur={handleBlur}
                      setFieldTouched={setFieldTouched}
                    />
                  ) : (
                    <HarshDriving
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      handleBlur={handleBlur}
                      setFieldTouched={setFieldTouched}
                    />
                  )}
                </div>
              </form>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

export default ScreenSensorEventDetection;
